import * as Sentry from '@sentry/react';

import { getDocBuilderContext } from '@/common/infrastructure/observability/docBuilderContext';

let correlationUserKey: string | undefined;

function generateRandomString(): string {
  const webCrypto: Crypto | undefined = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined;
  if (webCrypto?.getRandomValues) {
    const array = new Uint8Array(8);
    webCrypto.getRandomValues(array);
    return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return Math.random().toString(36).substring(2, 18);
}

export const getCorrelationId = (module?: string, submodule?: string): string => {
  const randomString = generateRandomString();
  const parts: string[] = [];

  if (module) {
    parts.push(module);
  }

  if (submodule) {
    parts.push(submodule);
  }

  parts.push(randomString);

  return parts.join('-');
};

export function setCorrelationUserKey(userId: string | null): void {
  correlationUserKey = userId && userId.length > 0 ? userId : undefined;
}

function getCorrelationIdStorageKey(): string | undefined {
  if (!correlationUserKey) {
    return undefined;
  }
  return `${correlationUserKey}-docBuilder-correlationId`;
}

export const generateCorrelationId = (): string | undefined => {
  try {
    const correlationIdKey = getCorrelationIdStorageKey();
    if (!correlationIdKey) {
      return undefined;
    }

    const { section, submodule } = getDocBuilderContext() ?? {};
    const correlationId = getCorrelationId(section, submodule);

    localStorage.setItem(correlationIdKey, correlationId);

    try {
      Sentry.setTag('correlationId', correlationId);
    } catch (sentryError) {
      console.error(sentryError);
    }

    return correlationId;
  } catch (error) {
    console.warn('Failed to regenerate correlationId', error);
    return undefined;
  }
};

export const getCorrelationIdFromStorage = (): string | undefined => {
  try {
    const correlationIdKey = getCorrelationIdStorageKey();
    if (correlationIdKey) {
      return localStorage.getItem(correlationIdKey) ?? undefined;
    }
  } catch {
    // localStorage unavailable
  }
  return undefined;
};

export const clearCorrelationId = (): void => {
  try {
    const correlationIdKey = getCorrelationIdStorageKey();
    if (correlationIdKey) {
      localStorage.removeItem(correlationIdKey);
    }

    try {
      Sentry.setTag('correlationId', '');
    } catch (sentryError) {
      console.warn('Failed to clear correlationId in Sentry', sentryError);
    }
  } catch (error) {
    console.warn('Failed to clear correlationId', error);
  }
};
